import {ethers} from 'ethers'
import {neoXBridgeABI} from '@/libs/x-bridge/NeoXBridgeABI'
import {BridgeNetworkHelper} from '@/helpers/BridgeNetworkHelper'
import {EnvHelper} from '@/helpers/EnvHelper'

/**
 * The withdrawal fee lives on the bridge contract and its operators move it
 * (`setNativeWithdrawalFee`/`setTokenWithdrawalFee`), so a fee kept in the env
 * goes stale silently: mainnet went to 0.03 GAS while the env still said 0.01,
 * and every X→N3 withdrawal died on gas estimation because the contract rejects
 * a `msg.value` under the fee — `withdrawNative` also refuses a `_maxFee` below
 * it. Read it from the chain and keep the env value only as the fallback for
 * when the RPC is unreachable.
 *
 * The official bridge hardcodes 0.1 GAS instead and lets the contract refund the
 * difference (verified on-chain: a 0.1 withdrawal only ever debits the 0.03 fee).
 * Sending the exact fee costs the user the same and keeps the wallet dialog
 * honest — a 0.1 there reads as a 0.1 charge. It also means a fee raised while
 * the modal is open reverts the withdrawal instead of quietly delivering less
 * than the amount that was quoted, which is why `withdrawNative` passes this
 * same value as `_maxFee` rather than a padded ceiling.
 */
export abstract class BridgeFeeHelper {
  private static cache = new Map<string, ethers.BigNumber>()

  /** Fee for an ERC-20 withdrawal, or the native (GAS) one when no token is given. */
  static async withdrawalFee(tokenHash?: string): Promise<ethers.BigNumber> {
    const key = `${BridgeNetworkHelper.network()}:${tokenHash?.toLowerCase() ??
      'native'}`

    const cached = this.cache.get(key)

    if (cached) {
      return cached
    }

    try {
      const contract = new ethers.Contract(
        BridgeNetworkHelper.xBridgeScriptHash(),
        neoXBridgeABI,
        this.provider()
      )

      const {config} = tokenHash
        ? await contract.tokenBridges(tokenHash)
        : await contract.nativeBridge()

      this.cache.set(key, config.fee)

      return config.fee
    } catch (error) {
      console.error('[BridgeFee] falling back to the configured fee', error)

      return this.fallbackFee()
    }
  }

  /** The same fee as a decimal string, for display and balance checks. */
  static async withdrawalFeeAmount(tokenHash?: string): Promise<string> {
    return ethers.utils.formatUnits(
      await this.withdrawalFee(tokenHash),
      EnvHelper.VUE_APP_DEFAULT_ETH_DECIMALS
    )
  }

  static clearCache() {
    this.cache.clear()
  }

  private static fallbackFee(): ethers.BigNumber {
    return ethers.utils.parseUnits(
      BridgeNetworkHelper.fee(),
      EnvHelper.VUE_APP_DEFAULT_ETH_DECIMALS
    )
  }

  private static provider() {
    return new ethers.providers.StaticJsonRpcProvider(
      BridgeNetworkHelper.xRpcUrl(),
      {
        chainId: BridgeNetworkHelper.xChainId(),
        name: 'neox',
      }
    )
  }
}
